(function () {
  if (window.StrideDragZoomWrapper) return;

  const MIN_ZOOM = 1;
  const MAX_ZOOM = 5;
  const DEFAULT_ZOOM = 1;
  const DOUBLE_TAP_ZOOM = 1.5;
  const DOUBLE_TAP_DELAY = 300;
  const DOUBLE_TAP_DISTANCE = 50;
  const DRAG_THRESHOLD = 10;

  class DragZoomWrapper extends HTMLElement {
  constructor() {
    super();
    this.scale = DEFAULT_ZOOM;
    this.initialDistance = 0;
    this.startScale = DEFAULT_ZOOM;
    this.translate = { x: 0, y: 0 };
    this.startPosition = { x: 0, y: 0 };
    this.startTranslate = { x: 0, y: 0 };
    this.isDragging = false;
    this.animationFrame = null;
    this.lastTapTime = 0;
    this.lastTapPosition = null;
    this.hasDraggedBeyondThreshold = false;
    this.hasManualZoom = false;
    this.controller = new AbortController();
  }

  get image() {
    return this.querySelector('img');
  }

  connectedCallback() {
    if (!this.image) return;

    // Only enable on mobile (touch devices)
    if (this.isMobile()) {
      this.initEventListeners();
      this.updateTransform();
    }

    // Reset zoom when wrapper is disconnected/reconnected
    this.setupResetListener();
  }

  disconnectedCallback() {
    this.controller.abort();
    this.cancelAnimationFrame();
  }

  /**
   * Check if device is mobile (has touch)
   */
  isMobile() {
    return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  }

  /**
   * Setup listener to reset zoom when dialog closes
   */
  setupResetListener() {
    const dialog = this.closest('dialog');
    if (dialog) {
      dialog.addEventListener('close', () => this.reset(), { once: true });
    }
  }

  /**
   * Initialize touch event listeners
   */
  initEventListeners() {
    const { signal } = this.controller;
    const options = { passive: false, signal };

    this.addEventListener('touchstart', (e) => this.handleTouchStart(e), options);
    this.addEventListener('touchmove', (e) => this.handleTouchMove(e), options);
    this.addEventListener('touchend', (e) => this.handleTouchEnd(e), options);
  }

  /**
   * Handle touch start
   */
  handleTouchStart(e) {
    const touchCount = e.touches.length;

    if (touchCount === 2) {
      e.preventDefault();
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      if (touch1 && touch2) {
        this.startZoomGesture(touch1, touch2);
      }
    } else if (touchCount === 1) {
      const touch = e.touches[0];
      if (!touch) return;

      const currentTime = performance.now();
      const timeSinceLastTap = currentTime - this.lastTapTime;

      // Check for double-tap
      if (timeSinceLastTap < DOUBLE_TAP_DELAY && this.lastTapPosition) {
        const distance = this.getDistance(touch, this.lastTapPosition);
        if (distance < DOUBLE_TAP_DISTANCE) {
          e.preventDefault();
          this.handleDoubleTap(touch);
          this.lastTapTime = 0;
          this.lastTapPosition = null;
          return;
        }
      }

      // Store tap info
      this.lastTapTime = currentTime;
      this.lastTapPosition = { x: touch.clientX, y: touch.clientY };
      if (this.scale > MIN_ZOOM) {
        e.preventDefault();
        this.startDragGesture(touch);
      } else {
        this.isDragging = false;
      }
    }
  }

  /**
   * Handle touch move
   */
  handleTouchMove(e) {
    const touchCount = e.touches.length;

    if (touchCount === 2) {
      e.preventDefault();
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      if (touch1 && touch2) {
        this.processZoomGesture(touch1, touch2);
      }
    } else if (touchCount === 1 && this.isDragging && this.scale > MIN_ZOOM) {
      e.preventDefault();
      const touch = e.touches[0];
      if (touch) {
        this.processDragGesture(touch);
      }
    }
  }

  /**
   * Handle touch end
   */
  handleTouchEnd(e) {
    if (e.touches.length === 0) {
      this.isDragging = false;
      this.hasDraggedBeyondThreshold = false;
      this.requestUpdateTransform();
    }
  }

  /**
   * Start zoom gesture with pinch
   */
  startZoomGesture(touch1, touch2) {
    this.initialDistance = this.getDistance(touch1, touch2);
    this.startScale = this.scale;
    this.isDragging = false;
  }

  /**
   * Process zoom gesture
   */
  processZoomGesture(touch1, touch2) {
    const midX = (touch1.clientX + touch2.clientX) / 2;
    const midY = (touch1.clientY + touch2.clientY) / 2;
    const currentDistance = this.getDistance(touch1, touch2);
    const oldScale = this.scale;

    // Calculate new scale
    const newScale = (currentDistance / this.initialDistance) * this.startScale;
    this.scale = this.clamp(newScale, MIN_ZOOM, MAX_ZOOM);
    this.hasManualZoom = true;

    // Adjust translation to keep pinch midpoint stationary
    const containerCenterX = this.clientWidth / 2;
    const containerCenterY = this.clientHeight / 2;
    const distanceFromCenterX = midX - containerCenterX;
    const distanceFromCenterY = midY - containerCenterY;
    const scaleDelta = this.scale / oldScale - 1.0;

    this.translate.x -= (distanceFromCenterX * scaleDelta) / this.scale;
    this.translate.y -= (distanceFromCenterY * scaleDelta) / this.scale;

    this.requestUpdateTransform();
    this.isDragging = false;
  }

  /**
   * Start drag gesture
   */
  startDragGesture(touch) {
    this.startPosition = { x: touch.clientX, y: touch.clientY };
    this.startTranslate = { x: this.translate.x, y: this.translate.y };
    this.isDragging = true;
    this.hasDraggedBeyondThreshold = false;
  }

  /**
   * Process drag gesture
   */
  processDragGesture(touch) {
    const distance = this.getDistance(touch, this.startPosition);

    if (!this.hasDraggedBeyondThreshold && distance < DRAG_THRESHOLD) {
      return;
    }

    this.hasDraggedBeyondThreshold = true;

    const dx = touch.clientX - this.startPosition.x;
    const dy = touch.clientY - this.startPosition.y;

    this.translate.x = this.startTranslate.x + dx / this.scale;
    this.translate.y = this.startTranslate.y + dy / this.scale;

    this.requestUpdateTransform();
  }

  /**
   * Handle double-tap zoom toggle
   */
  handleDoubleTap(touch) {
    let targetZoom;

    if (this.hasManualZoom) {
      targetZoom = MIN_ZOOM;
      this.hasManualZoom = false;
      this.translate = { x: 0, y: 0 };
    } else {
      const tolerance = 0.05;
      if (Math.abs(this.scale - MIN_ZOOM) < tolerance) {
        targetZoom = DOUBLE_TAP_ZOOM;
        this.hasManualZoom = true;
      } else {
        targetZoom = MIN_ZOOM;
        this.hasManualZoom = false;
      }
    }

    if (targetZoom !== MIN_ZOOM) {
      const oldScale = this.scale;
      this.scale = Math.min(MAX_ZOOM, targetZoom);

      const containerCenterX = this.clientWidth / 2;
      const containerCenterY = this.clientHeight / 2;
      const distanceFromCenterX = touch.clientX - containerCenterX;
      const distanceFromCenterY = touch.clientY - containerCenterY;
      const scaleDelta = this.scale / oldScale - 1.0;

      this.translate.x -= (distanceFromCenterX * scaleDelta) / this.scale;
      this.translate.y -= (distanceFromCenterY * scaleDelta) / this.scale;
    } else {
      this.scale = targetZoom;
      this.translate = { x: 0, y: 0 };
    }

    this.requestUpdateTransform();
  }

  /**
   * Update transform with constraints
   */
  updateTransform() {
    this.animationFrame = null;

    // Constrain scale
    this.scale = this.clamp(this.scale, MIN_ZOOM, MAX_ZOOM);

    // At minimum zoom, reset translation
    if (this.scale <= MIN_ZOOM) {
      this.translate.x = 0;
      this.translate.y = 0;
    } else {
      this.constrainTranslation();
    }

    // Apply CSS custom properties
    this.style.setProperty('--drag-zoom-scale', this.scale);
    this.style.setProperty('--drag-zoom-translate-x', `${this.translate.x}px`);
    this.style.setProperty('--drag-zoom-translate-y', `${this.translate.y}px`);
  }

  /**
   * Constrain translation to keep image within bounds
   */
  constrainTranslation() {
    if (!this.image) return;

    const wrapperRect = this.getBoundingClientRect();
    const naturalWidth = this.image.naturalWidth || wrapperRect.width;
    const naturalHeight = this.image.naturalHeight || wrapperRect.width;

    // Calculate image fit (object-fit: contain)
    const imageAspectRatio = naturalWidth / naturalHeight;
    const wrapperAspectRatio = wrapperRect.width / wrapperRect.height;

    let actualImageWidth, actualImageHeight;

    if (imageAspectRatio > wrapperAspectRatio) {
      actualImageWidth = wrapperRect.width;
      actualImageHeight = actualImageWidth / imageAspectRatio;
    } else {
      actualImageHeight = wrapperRect.height;
      actualImageWidth = actualImageHeight * imageAspectRatio;
    }

    const scaledImageWidth = actualImageWidth * this.scale;
    const scaledImageHeight = actualImageHeight * this.scale;

    const horizontalOverflow = Math.max(0, scaledImageWidth - wrapperRect.width);
    const verticalOverflow = Math.max(0, scaledImageHeight - wrapperRect.height);

    const maxTranslateX = horizontalOverflow / 2 / this.scale;
    const maxTranslateY = verticalOverflow / 2 / this.scale;

    this.translate.x = this.clamp(this.translate.x, -maxTranslateX, maxTranslateX);
    this.translate.y = this.clamp(this.translate.y, -maxTranslateY, maxTranslateY);
  }

  /**
   * Request animation frame for transform update
   */
  requestUpdateTransform() {
    if (!this.animationFrame) {
      this.animationFrame = requestAnimationFrame(() => this.updateTransform());
    }
  }

  /**
   * Cancel pending animation frame
   */
  cancelAnimationFrame() {
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
    }
  }

  /**
   * Reset zoom to default state
   */
  reset() {
    this.scale = DEFAULT_ZOOM;
    this.startScale = DEFAULT_ZOOM;
    this.translate = { x: 0, y: 0 };
    this.startPosition = { x: 0, y: 0 };
    this.startTranslate = { x: 0, y: 0 };
    this.isDragging = false;
    this.lastTapTime = 0;
    this.lastTapPosition = null;
    this.hasDraggedBeyondThreshold = false;
    this.hasManualZoom = false;

    this.style.setProperty('--drag-zoom-scale', DEFAULT_ZOOM);
    this.style.setProperty('--drag-zoom-translate-x', '0px');
    this.style.setProperty('--drag-zoom-translate-y', '0px');
  }

  /**
   * Helper: Get distance between two points
   */
  getDistance(point1, point2) {
    const x1 = point1.x ?? point1.clientX;
    const y1 = point1.y ?? point1.clientY;
    const x2 = point2.x ?? point2.clientX;
    const y2 = point2.y ?? point2.clientY;

    const dx = x1 - x2;
    const dy = y1 - y2;
    return Math.sqrt(dx * dx + dy * dy);
  }

  /**
   * Helper: Clamp value between min and max
   */
  clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }
  }

  window.StrideDragZoomWrapper = DragZoomWrapper;

  if (!customElements.get('drag-zoom-wrapper')) {
    customElements.define('drag-zoom-wrapper', DragZoomWrapper);
  }
})();
